import React from 'react';
import {
  Plugins,
  TypeImplementation,
  TypeCheckerState,
  getMeta,
  MetaAttributes,
  withMeta,
  setType,
  CheckerState,
  getSystem,
  withSystem,
} from '@packages/systems/wfdl';
import {TTypeConstructor} from '@packages/systems/wfdl-ast';
import {
  ContextualExpression,
  ContextualExpressions,
  stripAll,
  updateAnn,
} from '@packages/utilities/contextual-expression';
import {
  ExpressionEditor,
  getBindingType,
} from '@packages/systems/ExpressionEditor-utils';
import {qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {parseLabelsFromMeta} from '@packages/systems/plugins-utils/Boolean';
import {getPathToOperandFromConditionValue} from '@packages/systems/plugins-utils/Conditions';
import {DataTypeSummary} from '@packages/systems/ExpressionEditor-utils-ui';

const TBoolean = TTypeConstructor(
  qualify(Plugins.name('Boolean'), typeName('Boolean'))
);

/**
 * Retrieves the true/false labels from the condition source field's type.
 */
const getConditionSourceBooleanMeta = (
  rootCheckerState: CheckerState,
  rootContextualExpr: ContextualExpression | null,
  currentExpr: ContextualExpression
): MetaAttributes | null => {
  if (!rootContextualExpr) {
    return null;
  }
  // we need to retrieve the condition source field's type to determine the
  // labels to use for the true/false values
  const pathToOperandSelect = getPathToOperandFromConditionValue(currentExpr);
  const operandSelect = ContextualExpressions.getIn(
    rootContextualExpr,
    pathToOperandSelect
  );
  const operandType = operandSelect
    ? getBindingType(
        rootCheckerState.env.context.values,
        stripAll(operandSelect)
      )
    : null;
  const operandTypeMeta = operandType ? getMeta(operandType) : null;
  const {trueLabel, falseLabel} = parseLabelsFromMeta(operandTypeMeta);

  // if available, use the labels from the operand type
  if (trueLabel || falseLabel) {
    const conditionValueMeta: MetaAttributes = {};
    if (trueLabel) {
      conditionValueMeta.trueLabel = trueLabel;
    }
    if (falseLabel) {
      conditionValueMeta.falseLabel = falseLabel;
    }
    return conditionValueMeta;
  }
  return null;
};

function ConditionValueRender({
  expr,
  dispatch,
}: {
  expr: ContextualExpression;
  dispatch: TypeImplementation.ExpressionEditorDispatch;
}) {
  const [rootCheckerState, rootContextualExpr] =
    React.useContext(TypeCheckerState);
  const booleanMeta = getConditionSourceBooleanMeta(
    rootCheckerState,
    rootContextualExpr,
    expr
  );
  expr = updateAnn((ann) => {
    const type = ann.type;
    const system = type ? getSystem(type) : null;

    let updatedType = TBoolean;

    if (system) {
      updatedType = withSystem(system, updatedType);
    }

    if (booleanMeta) {
      updatedType = withMeta(booleanMeta, updatedType);
    }
    return {
      ...ann,
      type: updatedType,
    };
  }, expr);
  return <ExpressionEditor expression={expr} dispatch={dispatch} />;
}

export const ConditionValueAliasType = TypeImplementation.Alias({
  type: TBoolean,
  render: ConditionValueRender,
  renderSummary: function ConditionValueSummaryRender({expr}) {
    const [rootCheckerState, rootContextualExpr] =
      React.useContext(TypeCheckerState);
    const booleanMeta = getConditionSourceBooleanMeta(
      rootCheckerState,
      rootContextualExpr,
      expr
    );
    if (booleanMeta) {
      expr = setType(expr, withMeta(booleanMeta, TBoolean));
    }
    return <DataTypeSummary context={expr} />;
  },
  // we want to always delegate rendering the binding UI to ExpressionEditor
  // that is given a Boolean type with the correct labels, so that creating
  // a new prop from a Boolean condition value results in a new Boolean prop
  system: {bindUIVariant: 'custom'},
});
